import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Typography, Button} from '@mui/material'
import {Controller, useForm} from 'react-hook-form'
import {useParams} from 'react-router-dom'
import {DocumentUploadField, WorkExperienceList, skeletonBusyProps} from '../../../../components'
import {caregiverApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {caregiverDocumentCategories, parseError} from '../../../../utils/helpers'
import {
    backgroundDocumentSections,
    documentToEntry,
    documentUploadFormData,
    emptyBackgrounds,
    entryToExperience,
    experienceToEntry,
    toBackgroundValues,
    validateDocumentCategories,
} from '../../../../utils/caregiverBackgrounds'

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const [saving, setSaving] = useState(false)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const form = useForm({defaultValues: emptyBackgrounds})
    // Category of each saved document as the server last reported it, so Save
    // only sends what changed. Updated as each step of a save succeeds, so
    // retrying after a failure does not repeat finished work.
    const savedCategories = useRef({})

    // branchId is null until the branch list loads (e.g. on a page reload), and
    // these endpoints check the caregiver is in the branch. caregiverId is a dep
    // too, so navigating to another caregiver never shows or saves stale data.
    useEffect(() => {
        if (branchId) reset(true)
    }, [branchId, caregiverId])

    const showError = error => {
        const {title, description} = parseError(error)
        if (description) {
            setMessageBox({status: 'error', title, description})
        }
    }

    // Loading a caregiver shows the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        const id = ++requestId.current
        if (initial) {
            setSkeleton(true)
        } else {
            setLoading(true)
        }
        Promise.all([
            caregiverApi.getCaregiverBackgroundDocuments(branchId, caregiverId),
            caregiverApi.getCaregiverWorkExperiences(branchId, caregiverId),
        ])
            .then(([{data: documentData}, {data: experienceData}]) => {
                if (!initial) setLoading(false)
                if (id !== requestId.current) return
                setSkeleton(false)
                const documents = documentData?.documents ?? []
                savedCategories.current = Object.fromEntries(documents.map(doc => [doc.id, doc.category]))
                form.reset(toBackgroundValues(documents, experienceData?.work_experiences ?? []))
            })
            .catch(error => {
                if (!initial) setLoading(false)
                if (id !== requestId.current) return
                setSkeleton(false)
                showError(error)
            })
    }

    const updateDocument = (name, id, changes) =>
        form.setValue(
            name,
            form.getValues(name).map(entry => (entry.id === id ? {...entry, ...changes} : entry)),
        )

    const saveDocuments = async () => {
        const kept = new Set()
        for (const {name} of backgroundDocumentSections) {
            for (const entry of form.getValues(name)) {
                if (entry.documentId) kept.add(entry.documentId)
            }
        }

        for (const documentId of Object.keys(savedCategories.current)) {
            if (kept.has(documentId)) continue
            await caregiverApi.deleteCaregiverBackgroundDocument(branchId, caregiverId, documentId)
            delete savedCategories.current[documentId]
        }

        for (const {name, documentType} of backgroundDocumentSections) {
            for (const entry of form.getValues(name)) {
                if (entry.documentId) {
                    if (savedCategories.current[entry.documentId] === entry.category) continue
                    await caregiverApi.updateCaregiverBackgroundDocument(branchId, caregiverId, entry.documentId, {category: entry.category})
                    savedCategories.current[entry.documentId] = entry.category
                    continue
                }

                const formData = documentUploadFormData(entry, documentType)
                updateDocument(name, entry.id, {uploading: true, progress: 0, error: null})
                try {
                    const {data} = await caregiverApi.uploadCaregiverBackgroundDocument(branchId, caregiverId, formData, event => {
                        if (event.total) {
                            // Hold at 99 until the server answers.
                            updateDocument(name, entry.id, {progress: Math.min(99, Math.round((event.loaded / event.total) * 100))})
                        }
                    })
                    savedCategories.current[data.id] = data.category
                    updateDocument(name, entry.id, {...documentToEntry(data), id: entry.id, uploading: false})
                } catch (error) {
                    updateDocument(name, entry.id, {uploading: false, progress: 0, error: parseError(error).description || 'Upload failed'})
                    throw error
                }
            }
        }
    }

    const submit = async () => {
        if (!validateDocumentCategories(form)) return
        setSaving(true)
        try {
            const {data} = await caregiverApi.saveCaregiverWorkExperiences(branchId, caregiverId, {
                work_experiences: form.getValues('workExperiences').map(entryToExperience),
            })
            form.setValue('workExperiences', (data?.work_experiences ?? []).map(experienceToEntry))

            await saveDocuments()

            setMessageBox({
                status: 'success',
                title: 'Caregiver Informations Updated',
                description: "Caregiver's Backgrounds have been successfully updated. All changes are now reflected in the system.",
            })
        } catch (error) {
            showError(error)
        } finally {
            setSaving(false)
        }
    }

    const renderDocuments = (name, label, categories) => (
        <Controller
            name={name}
            control={form.control}
            render={({field}) => (
                <DocumentUploadField
                    label={label}
                    categories={categories}
                    value={field.value}
                    onChange={field.onChange}
                    simulateProgress={false}
                    disabled={saving}
                    loading={skeleton}
                />
            )}
        />
    )

    // A plain div rather than SkeletonContainer so the form and its fields stay mounted while loading.

    return (
        <div {...skeletonBusyProps(skeleton)}>
            {renderDocuments('governmentIds', 'Government ID', caregiverDocumentCategories.governmentId)}
            <br />
            {renderDocuments('criminalRecords', 'Criminal Record Check Report', caregiverDocumentCategories.criminalRecord)}
            <br />
            <br />
            <Controller
                name="workExperiences"
                control={form.control}
                render={({field}) => <WorkExperienceList value={field.value} onChange={field.onChange} disabled={saving} loading={skeleton} />}
            />
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                PROFESSIONAL CREDENTIALS
            </Typography>
            <br />
            {renderDocuments('licenses', 'Licenses or Certifications', caregiverDocumentCategories.licenses)}
            <br />
            <br />
            <br />
            <div className="actions">
                <Button variant="outlined" onClick={() => reset()} disabled={saving || skeleton}>
                    Reset
                </Button>
                <Button variant="contained" onClick={submit} disabled={saving || skeleton}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>
        </div>
    )
}
